import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

export interface Announcement {
  id: string;
  title: string;
  body: string;
  type: 'info' | 'warning' | 'success' | 'error';
  target: 'all' | 'owners' | 'admins';
  starts_at: string | null;
  ends_at: string | null;
  is_active: boolean;
  created_at: string;
  author?: { id: string; name: string } | null;
}

interface PaginatedAnnouncements {
  data: Announcement[];
  current_page: number;
  last_page: number;
  total: number;
}

export function useAdminAnnouncements(page = 1) {
  return useQuery({
    queryKey: ['admin', 'announcements', page],
    queryFn: async () => {
      const response = await api.get<PaginatedAnnouncements>(`/admin/announcements?page=${page}`);
      return response.data;
    },
  });
}

export function useActiveAnnouncements() {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['announcements', 'active'],
    queryFn: async () => {
      // The active-announcements endpoint is intentionally hosted at
      // `/announcements/active` (outside the /admin prefix) so non-admin
      // owners can fetch banners targeted to them.
      const response = await api.get<Announcement[]>('/announcements/active');
      return response.data;
    },
    // Only fetch once the user is authenticated. Firing while no token is
    // present returns 401, and api.ts wipes the auth token on any 401 — which
    // would log the owner out and spam retries. Gate on auth + no retry.
    enabled: isAuthenticated,
    retry: false,
    staleTime: 60_000,
  });
}
